/**
 * Session task progress (V1 Task Duration & Progress).
 *
 * At Stop Session the employee may report progress (0..100) on ANY task of
 * the session's project (no assignee filter). Each report is ONE row in
 * public.project_task_progress_events, keyed `${sessionId}:${taskId}`; the
 * database applies it to project_tasks.progress_percent. Progress is never
 * inferred from time worked and never moves dates.
 *
 * Integrity: this runs only AFTER the labour time log is saved, in its own
 * error boundary. A failure here never touches the time session. Retrying uses
 * the same idempotency keys, so a report is never recorded twice (a duplicate
 * key = already saved).
 *
 * Offline: progress is NOT queued. It is online-only, like every task write
 * ("Reconnect to change tasks"); callers show it as unavailable, never as a
 * fake success.
 */
import { supabase } from "../../supabase";
import { assertCan, CAPABILITY } from "../permissions";
import { isOffline } from "../connectivity";
import { shouldSkipSupabase } from "../guestMode";
import { describeProgressEvent, normalizeProgress, progressValue } from "./taskDuration";

export const PROGRESS_OFFLINE_MESSAGE =
  "Reconnect to report task progress. Your time is still saved.";
export const PROGRESS_FAILED_MESSAGE = "Task progress not saved — your time was saved.";

export function progressIdempotencyKey(sessionId, taskId) {
  return `${sessionId}:${taskId}`;
}

/**
 * Drafts ({ [taskId]: "40" | 40 | "" }) → reports for values that are set and
 * differ from the task's current progress. Invalid values are returned in
 * `invalid` (nothing is sent for them).
 */
export function buildProgressReports(tasks = [], drafts = {}) {
  const reports = [];
  const invalid = [];
  tasks.forEach((task) => {
    const raw = drafts[task.project_task_id];
    if (raw === undefined || raw === null || String(raw).trim() === "") return;
    const value = normalizeProgress(String(raw).trim());
    if (Number.isNaN(value) || value == null) {
      invalid.push(task.project_task_id);
      return;
    }
    const current = progressValue(task);
    if (current === value) return;
    reports.push({ taskId: task.project_task_id, percent: value, seenPercent: current });
  });
  return { reports, invalid };
}

function readableError(error) {
  const code = error?.code || "";
  if (code === "42501") return "You can't report progress on this task.";
  if (code === "23514") {
    if (/archived|OPERATIONAL_LOCK/i.test(error?.message || "")) return "This project is archived.";
    if (/session/i.test(error?.message || "")) return "Your time hasn't synced yet. Try again in a moment.";
    return "That progress value isn't allowed.";
  }
  if (/PGRST205|42P01|project_task_progress_events/.test(`${code} ${error?.message || ""}`)) {
    return "Task progress isn't available yet.";
  }
  return "Couldn't save this task's progress.";
}

/**
 * Send reports one by one. Returns { saved: [taskId], failed: [{ taskId, message }] }.
 * Never throws for a single report; throws only when offline (nothing sent).
 */
export async function submitSessionTaskProgress(actor, { sessionId, reports }) {
  assertCan(actor, CAPABILITY.PROJECT_TASK_COMPLETE);
  if (!sessionId) throw new Error("No session.");
  if (shouldSkipSupabase(actor)) return { saved: [], failed: [] };
  if (isOffline()) {
    const error = new Error(PROGRESS_OFFLINE_MESSAGE);
    error.code = "OFFLINE";
    throw error;
  }
  const saved = [];
  const failed = [];
  for (const report of reports || []) {
    try {
      const { error } = await supabase.from("project_task_progress_events").insert({
        project_task_id: report.taskId,
        session_id: sessionId,
        idempotency_key: progressIdempotencyKey(sessionId, report.taskId),
        progress_percent: report.percent,
        seen_percent: report.seenPercent ?? null,
      });
      if (error && error.code !== "23505") throw error;
      // 23505 = this session already reported this task (a retry): saved once.
      saved.push(report.taskId);
    } catch (error) {
      failed.push({ taskId: report.taskId, message: readableError(error) });
    }
  }
  return { saved, failed };
}

/**
 * Never throws: the outcome object the Stop Session UI shows. Used after the
 * time log is saved, so nothing here can corrupt or lose the session.
 *   { status: "saved" | "failed" | "offline", sessionId, reports, saved, failed }
 * `reports` on a failed/offline outcome are exactly the ones to retry (same keys).
 */
export async function reportSessionTaskProgress(actor, { sessionId, reports, titleOf = () => "Task" }) {
  try {
    const result = await submitSessionTaskProgress(actor, { sessionId, reports });
    if (result.failed.length) {
      return {
        status: "failed",
        sessionId,
        reports: reports.filter((r) => result.failed.some((f) => f.taskId === r.taskId)),
        saved: result.saved,
        failed: result.failed.map((f) => ({ ...f, title: titleOf(f.taskId) })),
      };
    }
    return { status: "saved", sessionId, reports: [], saved: result.saved, failed: [] };
  } catch (error) {
    return {
      status: error?.code === "OFFLINE" ? "offline" : "failed",
      sessionId,
      reports,
      saved: [],
      failed: [],
    };
  }
}

/* ── Time Log → task work history (read-only) ─────────────────────────────
 * A Time Log's "Tasks" are exactly the progress events recorded for it:
 * project_task_progress_events.time_log_id is stamped by the database at
 * report time (the reporter's own log for that session and project), and
 * previous_percent/progress_percent are captured there too. So the history is
 * read from the events alone: never from the task's current progress, never
 * inferred from hours, never split across tasks. Reads go through the normal
 * RLS of the signed-in user; this grants nothing new.
 */
export const TIME_LOG_TASKS_OFFLINE = "Reconnect to see the task progress reported in this session.";
export const TIME_LOG_TASKS_FAILED = "Couldn't load the task progress for this time log.";

const EVENT_SELECT =
  "progress_event_id, project_task_id, time_log_id, session_id, previous_percent, progress_percent, created_at, project_tasks(title, is_active)";

/** Events (as returned by the API) → display rows, oldest first. Pure. */
export function buildTimeLogTaskRows(events = [], timeLogId = null) {
  const seen = new Set();
  return (events || [])
    .filter((e) => e && (timeLogId == null || String(e.time_log_id) === String(timeLogId)))
    .filter((e) => {
      const key = e.progress_event_id || `${e.project_task_id}:${e.created_at}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((e) => {
      const change = describeProgressEvent(e);
      if (!change) return null;
      const task = Array.isArray(e.project_tasks) ? e.project_tasks[0] : e.project_tasks;
      return {
        id: e.progress_event_id || `${e.project_task_id}:${e.created_at}`,
        taskId: e.project_task_id,
        title: String(task?.title || "").trim() || "Task no longer available",
        archived: task ? task.is_active === false : false,
        at: e.created_at || null,
        ...change,
      };
    })
    .filter(Boolean)
    .sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")) || a.title.localeCompare(b.title));
}

/**
 * Load the progress reported in one Time Log's session.
 * → { status: "ready" | "offline" | "failed" | "none", rows }
 * "none" = nothing to look up (no synced log id, or guest mode). Never throws.
 */
export async function loadTimeLogTaskProgress(actor, timeLog) {
  const logId = timeLog?.id;
  if (logId == null || logId === "" || !/^\d+$/.test(String(logId))) return { status: "none", rows: [] };
  if (shouldSkipSupabase(actor)) return { status: "none", rows: [] };
  if (isOffline()) return { status: "offline", rows: [] };
  try {
    let query = supabase.from("project_task_progress_events").select(EVENT_SELECT).eq("time_log_id", logId);
    if (timeLog.organization_id) query = query.eq("organization_id", timeLog.organization_id);
    const { data, error } = await query.order("created_at", { ascending: true });
    if (error) {
      if (/PGRST205|42P01|project_task_progress_events/.test(`${error.code || ""} ${error.message || ""}`)) {
        return { status: "none", rows: [] };
      }
      throw error;
    }
    return { status: "ready", rows: buildTimeLogTaskRows(data || [], logId) };
  } catch (error) {
    console.error("TIME LOG TASK PROGRESS LOAD ERROR:", error?.code || error?.message || error);
    return { status: "failed", rows: [] };
  }
}
